import { useGLTF } from '@react-three/drei';
import { useFrame, type ThreeEvent } from '@react-three/fiber';
import { useEffect, useMemo, useRef, type MutableRefObject } from 'react';
import {
  AdditiveBlending,
  Color,
  type Group,
  type Mesh,
  type MeshStandardMaterial,
  type Sprite,
} from 'three';
import type { NewsStatus } from '@map/shared';
import { sceneColor } from '../styles/tokens.js';
import { glowTexture } from './planetSkin.js';
import type { Obstacle } from './TaskField.js';

/**
 * SAT-02. The Newswire satellite: the user's own model (see
 * models-src/README.md) on a tilted orbit of its own between the star and the
 * first planet, so it is always in frame and never in the rock belt.
 *
 * Its beacon says whether there is anything to read: a slow cyan pulse for
 * unseen headlines, a quick amber blink when a followed story has moved, and
 * nothing when all is read. Click (or tap) opens the Newswire.
 */

const MODEL = '/models/satellite.glb';

/** The model is ~1.56 units across; this makes it a small craft beside the star. */
const SCALE = 0.44;
const ORBIT_RADIUS = 1.95;
const ORBIT_TILT = 0.42;
/** One turn in about four minutes: alive, never busy. */
const ORBIT_SPEED = (Math.PI * 2) / 240;
/** Fixed start, so it is where it was on every load. */
const START_ANGLE = 2.4;

/** The FBX's materials by name, tinted from tokens.css (the bake leaves them white). */
const TINT: Record<string, Parameters<typeof sceneColor>[0]> = {
  OrangeMetal: 'sat-foil',
  WhiteMetal: 'sat-white',
  GrayMetal: 'sat-grey',
  BlackMetal: 'sat-black',
};

export interface SatelliteProps {
  status: NewsStatus | undefined;
  onOpen: () => void;
  still: boolean;
  /** A phone: a bigger invisible hit area, since a finger is not a pointer. */
  mobile: boolean;
  onHover: (hovered: boolean) => void;
  spots: MutableRefObject<Map<string, Obstacle>>;
}

export function Satellite({ status, onOpen, still, mobile, onHover, spots }: SatelliteProps) {
  const { scene } = useGLTF(MODEL);
  const model = useMemo(() => {
    const copy = scene.clone(true);
    copy.traverse((obj) => {
      const mesh = obj as Mesh;
      if (!mesh.isMesh) return;
      const material = (mesh.material as MeshStandardMaterial).clone();
      const tint = TINT[material.name];
      if (tint) material.color = new Color(sceneColor(tint, '#cccccc'));
      // The star is the only light worth having out here; keep the craft legible on its dark side.
      material.envMapIntensity = 0;
      material.emissive = new Color(sceneColor('sat-white', '#ffffff'));
      material.emissiveIntensity = 0.12;
      mesh.material = material;
    });
    return copy;
  }, [scene]);

  const glow = useMemo(() => glowTexture(), []);
  useEffect(() => () => glow?.dispose(), [glow]);
  const beaconColour = useMemo(
    () => ({
      fresh: new Color(sceneColor('sat-beacon', '#5fd4ff')),
      alert: new Color(sceneColor('sat-alert', '#ffb347')),
    }),
    [],
  );

  const pivot = useRef<Group>(null);
  const craft = useRef<Group>(null);
  const beacon = useRef<Sprite>(null);
  const angle = useRef(START_ANGLE);
  const clock = useRef(0);

  const alert = (status?.followingUnseen ?? 0) > 0;
  const fresh = (status?.unseen ?? 0) > 0;

  useEffect(() => {
    const map = spots.current;
    return () => {
      map.delete('satellite');
    };
  }, [spots]);

  useFrame((_, delta) => {
    clock.current += delta;
    if (!still) angle.current += delta * ORBIT_SPEED;
    const a = angle.current;
    if (craft.current) {
      craft.current.position.set(Math.cos(a) * ORBIT_RADIUS, 0, Math.sin(a) * ORBIT_RADIUS);
      // Panels face the star, the way a real one would sit.
      craft.current.rotation.y = -a + Math.PI / 2;
      if (!still) craft.current.rotation.z = Math.sin(clock.current * 0.4) * 0.08;
    }
    if (pivot.current && craft.current) {
      const world = craft.current.position.clone().applyMatrix4(pivot.current.matrixWorld);
      spots.current.set('satellite', { x: world.x, y: world.y, z: world.z, r: 0.35 });
    }
    if (beacon.current) {
      const material = beacon.current.material;
      if (alert) {
        material.color = beaconColour.alert;
        material.opacity = still ? 0.9 : Math.sin(clock.current * 6) > 0 ? 0.95 : 0.15;
      } else if (fresh) {
        material.color = beaconColour.fresh;
        material.opacity = still ? 0.85 : 0.5 + 0.45 * (0.5 + 0.5 * Math.sin(clock.current * 1.6));
      } else {
        material.opacity = 0;
      }
    }
  });

  const click = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    onOpen();
  };

  return (
    <group ref={pivot} rotation={[ORBIT_TILT, 0, 0.18]}>
      <group ref={craft}>
        <primitive object={model} scale={SCALE} />
        {glow && (
          <sprite ref={beacon} scale={[1.1, 1.1, 1]} position={[0, 0.12, 0]}>
            <spriteMaterial
              map={glow}
              transparent
              opacity={0}
              blending={AdditiveBlending}
              depthWrite={false}
            />
          </sprite>
        )}
        {/* The craft is small; clicks land on this instead. */}
        <mesh
          onClick={click}
          onPointerOver={() => {
            document.body.style.cursor = 'pointer';
            onHover(true);
          }}
          onPointerOut={() => {
            document.body.style.cursor = '';
            onHover(false);
          }}
        >
          <sphereGeometry args={[mobile ? 0.7 : 0.42, 12, 12]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      </group>
    </group>
  );
}

useGLTF.preload(MODEL);

/** The hover card: what is waiting up there. */
export function satelliteLabel(status: NewsStatus | undefined): string {
  if (!status) return 'Newswire';
  if (status.empty && status.refreshing) return 'Newswire · first scan under way';
  const parts = [`${status.unseen} new`];
  if (status.followingUnseen) parts.push(`${status.followingUnseen} on stories you follow`);
  if (status.refreshing) parts.push('scanning…');
  return `Newswire · ${parts.join(' · ')}`;
}
